import { useEffect, useState } from "react";
import { StringParam, useQueryParams } from "use-query-params";
import { z } from "zod";
import type { ClientsWorkOverviewView } from "@common/types/views/clientsWorkOverviewView";
import { useWorkLanding } from "./useWorkLanding";
import type { WorkLanding } from "./workLanding";
import { ALL_WORK_VIEW_ID } from "./workViewIds";

export interface WorkPageLanding extends WorkLanding {
    /** Replace the opened view's filters, keeping its sort and display preferences. */
    initialFilters?: ClientsWorkOverviewView["filters"];
}

const toClientLanding = (clientId: string): WorkPageLanding => ({
    landingViewId: undefined,
    initialBaseViewId: ALL_WORK_VIEW_ID,
    initialFilters: { workClientIds: [clientId] },
});

/**
 * Takes a `?clientId=` link (see `getOrganizationClientWorkLink`) off the
 * address bar and keeps the client it named. A value that isn't a UUID is
 * dropped, since the cached view only parses UUID client filters.
 */
function useClientWorkLink() {
    const [{ clientId }, setQueryParams] = useQueryParams({
        clientId: StringParam,
    });
    const [linkedClientId, setLinkedClientId] = useState<string | null>(null);

    useEffect(() => {
        if (!clientId) {
            return;
        }
        if (z.string().uuid().safeParse(clientId).success) {
            setLinkedClientId(clientId);
        }
        setQueryParams({ clientId: undefined }, "replaceIn");
    }, [clientId, setQueryParams]);

    return { isApplyingClientLink: Boolean(clientId), linkedClientId };
}

/**
 * The landing the Home (Work) page content mounts with, or `null` while the
 * page should show its loader: until the landing resolves, and while a
 * `?clientId=` link is applied, so the content remounts. After a client link
 * it opens on All work with only that client's filter, keeping the view's
 * sort and display preferences.
 */
export function useWorkPageLanding(): WorkPageLanding | null {
    const resolvedLanding = useWorkLanding();
    const [landing, setLanding] = useState(resolvedLanding);
    const { isApplyingClientLink, linkedClientId } = useClientWorkLink();

    if (landing === null && resolvedLanding !== null) {
        setLanding(resolvedLanding);
    }

    if (!landing || isApplyingClientLink) {
        return null;
    }

    return linkedClientId === null ? landing : toClientLanding(linkedClientId);
}
